/**
 * Properties Service
 * Handles property extraction and manipulation for Forge Viewer elements
 * 
 * This service provides comprehensive property inspection capabilities:
 * - Retrieve all properties for single or multiple elements
 * - Convert between Revit Element IDs and Forge Viewer DbIDs
 * - Search properties across all elements in the model
 * - Global property search with filter criteria
 * - Export properties to CSV and JSON formats
 * - Get surrounding elements within a specified radius
 * 
 * The service supports both DbID (Forge internal) and Revit Element ID
 * identification methods, making it flexible for different workflows.
 * All property retrieval uses async bulk APIs for optimal performance.
 */

import type { PropertyValue } from '@/store/propertiesStore';

export class PropertiesService {
  /**
   * Get all properties for a specific element
   */
  static async getElementProperties(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): Promise<PropertyValue[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    return new Promise((resolve, reject) => {
      viewer.getProperties(dbId, (result) => {
        if (!result) {
          reject(new Error('Failed to get properties'));
          return;
        }

        // Convert to our property format
        const properties: PropertyValue[] = result.properties.map(prop => ({
          displayName: prop.displayName,
          displayValue: prop.displayValue,
          displayCategory: prop.displayCategory,
          units: prop.units,
          type: prop.type,
          hidden: prop.hidden
        }));

        resolve(properties);
      }, (error) => {
        reject(error);
      });
    });
  }

  /**
   * Get properties for multiple elements (bulk operation)
   */
  static async getBulkProperties(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbIds: number[],
    propertyNames?: string[]
  ): Promise<Map<number, PropertyValue[]>> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    return new Promise((resolve, reject) => {
      viewer.model.getBulkProperties(
        dbIds,
        propertyNames || [],
        (results) => {
          const propertiesMap = new Map<number, PropertyValue[]>();
          
          results.forEach(result => {
            const properties: PropertyValue[] = result.properties.map(prop => ({
              displayName: prop.displayName,
              displayValue: prop.displayValue,
              displayCategory: prop.displayCategory,
              units: prop.units,
              type: prop.type,
              hidden: prop.hidden
            }));
            
            propertiesMap.set(result.dbId, properties);
          });

          resolve(propertiesMap);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  /**
   * Find DbId by Revit Element ID
   */
  static async findDbIdByRevitId(
    viewer: Autodesk.Viewing.GuiViewer3D,
    revitId: string
  ): Promise<number | null> {
    if (!viewer || !viewer.model) {
      console.error('[PropertiesService] Viewer or model not available');
      return null;
    }

    try {
      // Search for elements with matching Revit Element ID
      const dbIds = await this.searchByProperty(viewer, 'Element ID', revitId, true);
      
      if (dbIds.length > 0) {
        console.log(`[PropertiesService] Found DbId ${dbIds[0]} for Revit ID ${revitId}`);
        return dbIds[0];
      }
      
      console.warn(`[PropertiesService] No element found with Revit ID: ${revitId}`);
      return null;
    } catch (error) {
      console.error(`[PropertiesService] Error finding DbId for Revit ID ${revitId}:`, error);
      return null;
    }
  }

  /**
   * Search for elements by property value
   */
  static async searchByProperty(
    viewer: Autodesk.Viewing.GuiViewer3D,
    propertyName: string,
    value: string | number,
    exactMatch: boolean = false
  ): Promise<number[]> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    const matchingDbIds: number[] = [];
    const batchSize = 100;

    // Search in batches
    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);

      await new Promise<void>((resolve) => {
        viewer.model.getBulkProperties(batch, [propertyName], (results) => {
          results.forEach(result => {
            if (result.properties && result.properties.length > 0) {
              const prop = result.properties[0];
              if (prop && prop.displayValue !== undefined) {
                const propValue = String(prop.displayValue).toLowerCase();
                const searchValue = String(value).toLowerCase();
                
                const matches = exactMatch 
                  ? propValue === searchValue
                  : propValue.includes(searchValue);
                
                if (matches) {
                  matchingDbIds.push(result.dbId);
                }
              }
            }
          });
          resolve();
        }, (error) => {
          console.error('Error in bulk properties search:', error);
          resolve();
        });
      });
    }

    return matchingDbIds;
  }

  /**
   * Export properties to CSV format
   */
  static exportToCSV(
    elementName: string,
    properties: PropertyValue[]
  ): void {
    // Create CSV content
    const headers = ['Category', 'Property', 'Value', 'Units'];
    const rows = properties
      .filter(prop => !prop.hidden)
      .map(prop => [
        prop.displayCategory || 'Other',
        prop.displayName,
        prop.displayValue || '',
        prop.units || ''
      ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape commas and quotes in cell values
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `properties_${elementName.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export properties to JSON format
   */
  static exportToJSON(
    elementName: string,
    dbId: number,
    properties: PropertyValue[]
  ): void {
    const exportData = {
      timestamp: new Date().toISOString(),
      element: {
        dbId: dbId,
        name: elementName
      },
      properties: properties.reduce((acc, prop) => {
        const category = prop.displayCategory || 'Other';
        if (!acc[category]) {
          acc[category] = {};
        }
        acc[category][prop.displayName] = {
          value: prop.displayValue,
          units: prop.units,
          hidden: prop.hidden
        };
        return acc;
      }, {} as Record<string, any>)
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json' 
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `properties_${elementName.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Find element by Revit Element ID
   */
  static async findElementByRevitId(
    viewer: Autodesk.Viewing.GuiViewer3D,
    revitId: string
  ): Promise<number | null> {
    if (!viewer || !viewer.model) {
      throw new Error('Viewer or model not available');
    }

    const tree = viewer.model.getInstanceTree();
    if (!tree) {
      throw new Error('Model tree not available');
    }

    // Get all element IDs
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      allDbIds.push(dbId);
    }, true);

    // Search for element with matching Revit ID
    // Revit ID is typically stored as "Element ID" or "Id" property
    const propertyNames = ['Element ID', 'Id', 'ElementId', 'Revit Id'];
    
    for (const propName of propertyNames) {
      const matchingDbIds = await this.searchByProperty(viewer, propName, revitId, true);
      if (matchingDbIds.length > 0) {
        return matchingDbIds[0]; // Return first match
      }
    }

    // If not found in common properties, do a more thorough search
    const batchSize = 50;
    for (let i = 0; i < allDbIds.length; i += batchSize) {
      const batch = allDbIds.slice(i, i + batchSize);
      
      const found = await new Promise<number | null>((resolve) => {
        viewer.model.getBulkProperties(batch, [], (results) => {
          for (const result of results) {
            // Check all properties for Revit ID
            const idProp = result.properties.find(p => 
              (p.displayName === 'Element ID' || 
               p.displayName === 'Id' || 
               p.displayName === 'ElementId' ||
               p.displayName === 'Revit Id') &&
              String(p.displayValue) === revitId
            );
            
            if (idProp) {
              resolve(result.dbId);
              return;
            }
          }
          resolve(null);
        }, () => resolve(null));
      });
      
      if (found !== null) {
        return found;
      }
    }

    return null;
  }

  /**
   * Compare properties between two elements
   */
  static compareProperties(
    properties1: PropertyValue[],
    properties2: PropertyValue[]
  ): {
    common: PropertyValue[];
    unique1: PropertyValue[];
    unique2: PropertyValue[];
    different: Array<{
      name: string;
      value1: any;
      value2: any;
    }>;
  } {
    const map1 = new Map(properties1.map(p => [p.displayName, p]));
    const map2 = new Map(properties2.map(p => [p.displayName, p]));

    const common: PropertyValue[] = [];
    const different: Array<{ name: string; value1: any; value2: any }> = [];
    const unique1: PropertyValue[] = [];
    const unique2: PropertyValue[] = [];

    // Find common and different properties
    map1.forEach((prop1, name) => {
      if (map2.has(name)) {
        const prop2 = map2.get(name)!;
        if (prop1.displayValue === prop2.displayValue) {
          common.push(prop1);
        } else {
          different.push({
            name: name,
            value1: prop1.displayValue,
            value2: prop2.displayValue
          });
        }
      } else {
        unique1.push(prop1);
      }
    });

    // Find properties unique to second element
    map2.forEach((prop2, name) => {
      if (!map1.has(name)) {
        unique2.push(prop2);
      }
    });

    return { common, unique1, unique2, different };
  }
}